import React, { useState, useCallback, useRef } from 'react';
import { X, Camera, Upload, Image as ImageIcon, AlertCircle, CheckCircle, Loader, Link2, Zap } from 'lucide-react';
import apiService from '../../services/api';
import { formatDate } from '../../utils/dateUtils';
import BaseModal from '../common/BaseModal';
import { compressImage } from '../../utils/imageCompression';

const ReceiptScanModal = ({ isOpen, onClose, account, onItemsExtracted }) => {
  const [dragActive, setDragActive] = useState(false);
  const [receiptImage, setReceiptImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [step, setStep] = useState('upload'); // 'upload', 'processing', 'preview', 'complete'
  const [extractedItems, setExtractedItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [error, setError] = useState(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [receiptMetadata, setReceiptMetadata] = useState(null);
  const [autoMatchResult, setAutoMatchResult] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Reset state when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setDragActive(false);
      setReceiptImage(null);
      setImagePreview(null);
      setStep('upload');
      setExtractedItems([]);
      setSelectedItems(new Set());
      setError(null);
      setProcessingProgress(0);
      setReceiptMetadata(null);
    }
  }, [isOpen]);

  const handleClose = async () => {
    // If we completed successfully, trigger the callback to refresh the list BEFORE closing
    const wasSuccessful = step === 'complete';

    // Reset state
    setDragActive(false);
    setReceiptImage(null);
    setImagePreview(null);
    setStep('upload');
    setExtractedItems([]);
    setSelectedItems(new Set());
    setError(null);
    setProcessingProgress(0);
    setReceiptMetadata(null);

    if (wasSuccessful && onItemsExtracted) {
      // Call the callback which will handle closing
      await onItemsExtracted();
    } else {
      // If cancelled/error, just close normally
      onClose();
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleImageSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleImageSelect = async (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB (Claude Vision API limit)

    if (!validTypes.includes(file.type)) {
      setError('Please upload a valid image file (JPG, PNG, HEIC, or WebP)');
      return;
    }

    setError(null);

    try {
      let processedFile = file;

      // Always compress images from mobile cameras, or if larger than 5MB
      // Mobile cameras often produce large files even if reported type is correct
      const shouldCompress = file.size > maxSize || file.size > 3 * 1024 * 1024; // Compress if > 3MB to be safe

      if (shouldCompress) {
        setError(`Compressing image... (original: ${(file.size / 1024 / 1024).toFixed(1)}MB)`);
        processedFile = await compressImage(file, 5);
        setError(null);
      }

      // Final validation
      if (processedFile.size > maxSize) {
        throw new Error(`Image is still too large: ${(processedFile.size / 1024 / 1024).toFixed(2)}MB. Maximum is 5MB.`);
      }

      setReceiptImage(processedFile);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(processedFile);
    } catch (err) {
      console.error('Error processing image:', err);
      setError('Failed to process image: ' + err.message);
    }
  };

  const processReceipt = async () => {
    if (!receiptImage || !account) {
      setError('Missing receipt image or account information');
      return;
    }

    setStep('processing');
    setError(null);
    setProcessingProgress(10);

    try {
      const formData = new FormData();
      formData.append('image', receiptImage);
      formData.append('account_id', account.id);

      setProcessingProgress(30);

      const response = await apiService.analyzeReceiptImage(formData, (uploadProgress) => {
        setProcessingProgress(30 + (uploadProgress * 0.2)); // 30-50% for upload
      });

      setProcessingProgress(50);

      // Poll for task completion
      const result = await pollTaskStatus(response.task_id);

      if (result && result.items) {
        setExtractedItems(result.items);
        setReceiptMetadata(result.metadata);

        // Select all items by default
        const allIndices = result.items.map((_, index) => index);
        setSelectedItems(new Set(allIndices));

        setStep('preview');
      } else {
        throw new Error('No items extracted from receipt');
      }

    } catch (err) {
      console.error('Error processing receipt:', err);
      setError(err.message || 'Failed to process receipt');
      setStep('upload');
    }
  };

  const pollTaskStatus = async (taskId) => {
    const maxPollingTime = 60000; // 1 minute
    const pollingInterval = 2000; // 2 seconds
    const startTime = Date.now();

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const status = await apiService.getTaskStatus(taskId);

          if (status.state === 'SUCCESS') {
            setProcessingProgress(100);
            resolve(status.result);
          } else if (status.state === 'FAILURE') {
            reject(new Error(status.error || 'Receipt processing failed'));
          } else if (status.state === 'PROGRESS') {
            const progress = 50 + ((status.progress || 0) / 100) * 50;
            setProcessingProgress(progress);

            if (Date.now() - startTime < maxPollingTime) {
              setTimeout(poll, pollingInterval);
            } else {
              reject(new Error('Processing timeout - please try again'));
            }
          } else {
            setProcessingProgress(55);

            if (Date.now() - startTime < maxPollingTime) {
              setTimeout(poll, pollingInterval);
            } else {
              reject(new Error('Processing timeout - please try again'));
            }
          }
        } catch (err) {
          console.error('Error polling task status:', err);
          reject(new Error(err.message || 'Failed to get processing status'));
        }
      };

      setTimeout(poll, pollingInterval);
    });
  };

  const createTransactions = async () => {
    setStep('processing');
    setError(null);

    try {
      const itemsToImport = extractedItems.filter((_, index) =>
        selectedItems.has(index)
      );

      const response = await apiService.importReceiptItems(account.id, {
        items: itemsToImport,
        metadata: receiptMetadata
      });

      // Store auto-match result if present
      if (response.auto_match) {
        setAutoMatchResult(response.auto_match);
      }

      setStep('complete');
      onItemsExtracted?.(response);

    } catch (err) {
      console.error('Error creating transactions:', err);
      setError(err.message || 'Failed to create transactions');
      setStep('preview');
    }
  };

  const toggleItemSelection = (index) => {
    const newSelection = new Set(selectedItems);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    setSelectedItems(newSelection);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: account?.currency || 'USD'
    }).format(amount || 0);
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={step === 'processing' ? () => {} : handleClose}
      size="lg"
      showCloseButton={false}
      closeOnBackdrop={step !== 'processing'}
      contentClassName="p-0"
    >
      <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
          Scan Receipt
        </h2>
        <button
          type="button"
          onClick={handleClose}
          className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 p-2"
          aria-label="Close modal"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

        <div className="p-4 sm:p-6">
          {/* Progress Steps - Hidden on mobile for cleaner UI */}
          <div className="hidden sm:flex items-center justify-center mb-8">
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                step === 'upload' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'
              }`}>
                <span className="text-sm font-medium">1</span>
              </div>
              <div className={`h-1 w-16 mx-2 ${
                ['processing', 'preview', 'complete'].includes(step) ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
              }`} />
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                step === 'processing' ? 'bg-blue-600 text-white' :
                ['preview', 'complete'].includes(step) ? 'bg-green-600 text-white' : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
              }`}>
                <span className="text-sm font-medium">2</span>
              </div>
              <div className={`h-1 w-16 mx-2 ${
                ['preview', 'complete'].includes(step) ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
              }`} />
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                step === 'preview' ? 'bg-blue-600 text-white' :
                step === 'complete' ? 'bg-green-600 text-white' : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
              }`}>
                <span className="text-sm font-medium">3</span>
              </div>
            </div>
          </div>

          {/* Step Content */}
          {step === 'upload' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="text-center sm:block hidden">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Upload Receipt Image</h3>
                <p className="text-gray-600 dark:text-gray-400">Take a photo or upload an existing receipt image</p>
              </div>

              {/* Image Upload/Capture Area */}
              {!receiptImage ? (
                <div
                  className={`border-2 border-dashed rounded-lg p-6 sm:p-8 text-center transition-colors ${
                    dragActive
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <div className="space-y-4 sm:space-y-6">
                    <ImageIcon className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400 dark:text-gray-500 mx-auto" />
                    <div className="space-y-3 sm:space-y-4">
                      {/* Mobile: Stack buttons vertically with camera primary */}
                      <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                        <button
                          type="button"
                          onClick={() => cameraInputRef.current?.click()}
                          className="inline-flex items-center justify-center px-6 py-3 sm:px-4 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-base sm:text-sm shadow-lg sm:shadow-none"
                        >
                          <Camera className="h-6 w-6 sm:h-5 sm:w-5 mr-2" />
                          Take Photo
                        </button>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="inline-flex items-center justify-center px-6 py-3 sm:px-4 sm:py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium text-base sm:text-sm"
                        >
                          <Upload className="h-6 w-6 sm:h-5 sm:w-5 mr-2" />
                          Upload Image
                        </button>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
                        or drag and drop an image here
                      </p>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Supports JPG, PNG, HEIC, WebP (max 5MB)</p>
                  </div>

                  <input
                    ref={cameraInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => e.target.files?.[0] && handleImageSelect(e.target.files[0])}
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => e.target.files?.[0] && handleImageSelect(e.target.files[0])}
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <img
                      src={imagePreview}
                      alt="Receipt preview"
                      className="w-full h-auto max-h-96 object-contain bg-gray-100 dark:bg-gray-900"
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {receiptImage.name} ({(receiptImage.size / 1024).toFixed(1)} KB)
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setReceiptImage(null);
                        setImagePreview(null);
                      }}
                      className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                    >
                      Remove Image
                    </button>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 sm:p-4">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0" />
                    <span className="text-sm sm:text-base text-red-700 dark:text-red-400">{error}</span>
                  </div>
                </div>
              )}

              {/* Tips - Simplified for mobile */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3 sm:p-4">
                <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-2 text-sm sm:text-base">Tips for Best Results:</h4>
                <ul className="text-blue-800 dark:text-blue-300 text-xs sm:text-sm space-y-1 list-disc list-inside">
                  <li>Well-lit and in focus</li>
                  <li>Capture entire receipt</li>
                  <li className="hidden sm:list-item">Avoid shadows and glare</li>
                  <li className="hidden sm:list-item">Make sure text is clearly readable</li>
                </ul>
              </div>
            </div>
          )}

          {step === 'processing' && (
            <div className="space-y-6">
              <div className="text-center">
                <Loader className="h-16 w-16 text-blue-600 mx-auto animate-spin" />
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Processing Receipt</h3>
                  <p className="text-gray-600 dark:text-gray-400">Using AI to extract items and prices...</p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                  <span>Progress</span>
                  <span>{Math.round(processingProgress)}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${processingProgress}%` }}
                  />
                </div>
              </div>

              {imagePreview && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <img
                    src={imagePreview}
                    alt="Receipt being processed"
                    className="w-full h-auto max-h-64 object-contain bg-gray-100 dark:bg-gray-900"
                  />
                </div>
              )}
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Review Receipt Items</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  This will create ONE transaction for the total amount with {extractedItems.length} itemized items.
                </p>
              </div>

              {receiptMetadata && (
                <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {receiptMetadata.merchant && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Merchant:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{receiptMetadata.merchant}</span>
                      </div>
                    )}
                    {receiptMetadata.date && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Date:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{receiptMetadata.date}</span>
                      </div>
                    )}
                    {receiptMetadata.total && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Total:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{formatCurrency(receiptMetadata.total)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="bg-gray-50 dark:bg-gray-900 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedItems.size === extractedItems.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedItems(new Set(extractedItems.map((_, i) => i)));
                        } else {
                          setSelectedItems(new Set());
                        }
                      }}
                      className="rounded border-gray-300 dark:border-gray-600 text-blue-600 mr-3"
                    />
                    <span className="font-medium text-gray-900 dark:text-white">Select All ({selectedItems.size} of {extractedItems.length})</span>
                  </label>
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {extractedItems.map((item, index) => (
                    <div key={index} className="border-b border-gray-200 dark:border-gray-700 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-900">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedItems.has(index)}
                          onChange={() => toggleItemSelection(index)}
                          className="rounded border-gray-300 dark:border-gray-600 text-blue-600 mr-3"
                        />
                        <div className="flex-1 flex justify-between items-center">
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">{item.description}</div>
                            {item.quantity > 1 && (
                              <div className="text-sm text-gray-600 dark:text-gray-400">Quantity: {item.quantity}</div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {formatCurrency(item.price)}
                            </div>
                            {item.category && (
                              <span className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full">
                                {item.category}
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 'complete' && (
            <div className="space-y-6">
              <div className="text-center">
                <CheckCircle className="h-16 w-16 text-green-600 dark:text-green-500 mx-auto" />
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Receipt Imported!</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Successfully imported {selectedItems.size} itemized items.
                  </p>
                </div>
              </div>

              {/* Auto-Match Result */}
              {autoMatchResult && (
                <div className={`rounded-lg border p-4 ${
                  autoMatchResult.matched
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                }`}>
                  <div className="flex items-start gap-3">
                    {autoMatchResult.matched ? (
                      <Link2 className="h-5 w-5 text-green-600 dark:text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <Zap className="h-5 w-5 text-blue-600 dark:text-blue-500 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      {autoMatchResult.matched ? (
                        <>
                          <h4 className="text-sm font-medium text-green-900 dark:text-green-100 mb-1">
                            Automatically Linked!
                          </h4>
                          <p className="text-xs text-green-700 dark:text-green-300 mb-2">
                            Matched to transaction with {autoMatchResult.match_score}/20 score ({autoMatchResult.confidence})
                          </p>
                          <div className="bg-white dark:bg-gray-800 rounded border border-green-200 dark:border-green-700 p-2 space-y-1">
                            <div className="text-xs font-medium text-gray-900 dark:text-white">
                              {autoMatchResult.transaction_description}
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                              <span>{autoMatchResult.account_name}</span>
                              <span>{formatCurrency(autoMatchResult.transaction_amount)}</span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-500">
                              {formatDate(autoMatchResult.transaction_date)}
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                            Possible Match Found
                          </h4>
                          <p className="text-xs text-blue-700 dark:text-blue-300 mb-2">
                            Score {autoMatchResult.suggestion.match_score}/20 ({autoMatchResult.suggestion.confidence}) - Not auto-linked
                          </p>
                          <div className="bg-white dark:bg-gray-800 rounded border border-blue-200 dark:border-blue-700 p-2 space-y-1">
                            <div className="text-xs font-medium text-gray-900 dark:text-white">
                              {autoMatchResult.suggestion.transaction_description}
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                              <span>{autoMatchResult.suggestion.account_name}</span>
                              <span>{formatCurrency(autoMatchResult.suggestion.transaction_amount)}</span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-500">
                              {formatDate(autoMatchResult.suggestion.transaction_date)}
                            </div>
                          </div>
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                            You can manually link this from the Receipts page.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions - Optimized for mobile touch */}
        <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          {step === 'upload' && (
            <>
              <button
                type="button"
                onClick={handleClose}
                className="px-5 sm:px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={processReceipt}
                disabled={!receiptImage || !account}
                className="px-6 sm:px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg sm:shadow-none"
              >
                Process Receipt
              </button>
            </>
          )}

          {step === 'preview' && (
            <>
              <button
                type="button"
                onClick={() => setStep('upload')}
                className="px-5 sm:px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Back
              </button>
              <button
                type="button"
                onClick={createTransactions}
                disabled={selectedItems.size === 0}
                className="px-6 sm:px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg sm:shadow-none"
              >
                Import ({selectedItems.size})
              </button>
            </>
          )}

          {step === 'complete' && (
            <button
              type="button"
              onClick={handleClose}
              className="w-full sm:w-auto px-6 py-3 sm:py-2 text-base sm:text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 shadow-lg sm:shadow-none"
            >
              Done
            </button>
          )}
        </div>
    </BaseModal>
  );
};

export default ReceiptScanModal;
